import { defineStore } from "pinia";
import { ref } from "vue";
import { extractError } from "@ram/api-client";
import { api } from "@/lib/api";

export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  role: string | null;
  party: string | null;
  party_group: number | null;
  is_staff: boolean;
  modules: string[];
  surfaces: string[];
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<CurrentUser | null>(null);
  const initialised = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);
  /** Backend error code (e.g. `pending_approval`, `email_not_verified`,
   *  `rejected`, `auth_failed`) — used by the SPA to pick branch-specific
   *  UI without reparsing the message string. */
  const errorCode = ref<string | null>(null);
  /** Free-form details from the backend envelope — e.g. `reason` for
   *  rejected, or `captcha_required` for the captcha gate. */
  const errorDetails = ref<Record<string, unknown> | null>(null);
  /** When the backend signals captcha_required, the SPA renders the widget on next try. */
  const captchaRequired = ref(false);
  const siteKey = ref<string | null>(null);

  async function bootstrap() {
    try {
      const { data } = await api.get<CurrentUser>("/api/v1/auth/me");
      user.value = data;
    } catch {
      user.value = null;
    } finally {
      initialised.value = true;
    }
  }

  async function login(payload: {
    email: string;
    password: string;
    remember: boolean;
    captcha_token?: string;
  }) {
    loading.value = true;
    error.value = null;
    errorCode.value = null;
    errorDetails.value = null;
    try {
      const { data } = await api.post<CurrentUser>("/api/v1/auth/login", payload);
      user.value = data;
      captchaRequired.value = false;
      siteKey.value = null;
    } catch (e: unknown) {
      const envelope = extractError(e);
      error.value = envelope.message;
      errorCode.value = envelope.code;
      errorDetails.value = envelope.details ?? null;
      const details = envelope.details as
        | { captcha_required?: boolean; site_key?: string }
        | undefined;
      if (details?.captcha_required) {
        captchaRequired.value = true;
        siteKey.value = details.site_key ?? null;
      }
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function resendActivation(email: string): Promise<void> {
    // Always silent on the network side — backend returns 202 regardless of
    // whether the email exists. UI just shows "If we know this email, a
    // fresh link is on its way."
    await api.post("/api/v1/auth/activate/resend", { email }).catch(() => {});
  }

  async function logout() {
    await api.post("/api/v1/auth/logout").catch(() => {});
    user.value = null;
  }

  async function signup(payload: {
    email: string;
    password: string;
    name: string;
    mobile: string;
    department?: string;
    captcha_token: string;
  }) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{ message: string }>("/api/v1/auth/signup", payload);
      return data;
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function activate(token: string) {
    loading.value = true;
    error.value = null;
    try {
      await api.post("/api/v1/auth/activate", { token });
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function forgot(email: string, captcha_token: string) {
    loading.value = true;
    error.value = null;
    try {
      await api.post("/api/v1/auth/forgot", { email, captcha_token });
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  // Note: named `resetPassword` (not `reset`) to avoid clashing with
  // Pinia's reserved `$reset` action that resets store state.
  async function resetPassword(token: string, new_password: string) {
    loading.value = true;
    error.value = null;
    try {
      await api.post("/api/v1/auth/reset", { token, new_password });
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function privateSignupRequest(payload: {
    mobile: string;
    name: string;
    email?: string;
    captcha_token: string;
  }) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{ message: string; mobile_masked: string }>(
        "/api/v1/auth/private/signup",
        payload,
      );
      return data;
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function privateSignupVerify(mobile: string, otp: string) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<CurrentUser>("/api/v1/auth/private/signup/verify", {
        mobile,
        otp,
      });
      user.value = data;
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function privateSigninRequest(mobile: string, captcha_token: string) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{ message: string; mobile_masked: string }>(
        "/api/v1/auth/private/signin",
        { mobile, captcha_token },
      );
      return data;
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function privateSigninVerify(mobile: string, otp: string) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<CurrentUser>("/api/v1/auth/private/signin/verify", {
        mobile,
        otp,
      });
      user.value = data;
    } catch (e) {
      error.value = extractError(e).message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function has(moduleCode: string): boolean {
    return Boolean(user.value?.modules.includes(moduleCode));
  }

  return {
    user,
    initialised,
    loading,
    error,
    errorCode,
    errorDetails,
    captchaRequired,
    siteKey,
    bootstrap,
    login,
    logout,
    signup,
    activate,
    forgot,
    resetPassword,
    resendActivation,
    privateSignupRequest,
    privateSignupVerify,
    privateSigninRequest,
    privateSigninVerify,
    has,
  };
});
